import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, ChevronUp } from "lucide-react";
import api from "../api";
import { useI18n } from "../i18n";

const PREVIEW_COUNT = 3;

const Gallery = () => {
  const { t, lang } = useI18n();
  const [photos, setPhotos] = useState([]);
  const [photographer, setPhotographer] = useState("Zangar Skakov");
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let cancel = false;
    api
      .get("/gallery")
      .then((res) => {
        if (cancel) return;
        setPhotos(res.data.photos || []);
        if (res.data.photographer) setPhotographer(res.data.photographer);
      })
      .catch((e) => console.error("Failed to load gallery", e));
    return () => {
      cancel = true;
    };
  }, []);

  const preview = photos.slice(0, PREVIEW_COUNT);
  const rest = photos.slice(PREVIEW_COUNT);
  const hasMore = rest.length > 0;

  return (
    <section
      id="gallery"
      data-testid="gallery-section"
      className="relative py-24 md:py-32"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="mb-12">
          <div className="kicker mb-4">{t.gallery.kicker}</div>
          <h2
            className="font-display text-5xl sm:text-6xl lg:text-7xl tracking-tighter"
            data-testid="gallery-title"
          >
            {t.gallery.title}
          </h2>
          <p className="mt-4 text-white/60 max-w-md">{t.gallery.subtitle}</p>
        </div>

        {/* Preview row — 3 photos */}
        <div
          className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4"
          data-testid="gallery-preview"
        >
          {preview.map((url, i) => (
            <motion.div
              key={`p-${i}`}
              initial={{ opacity: 0, scale: 0.98 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, amount: 0.1 }}
              transition={{ duration: 0.5, delay: i * 0.06 }}
              className="relative overflow-hidden bg-[#161616] group aspect-[3/4] sm:aspect-[4/5]"
              data-testid={`gallery-img-${i}`}
            >
              <img
                src={url}
                alt={`Team Astana — photo by ${photographer}`}
                loading="lazy"
                referrerPolicy="no-referrer"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors" />
            </motion.div>
          ))}
        </div>

        {/* Expandable rest */}
        {hasMore && (
          <>
            <AnimatePresence initial={false}>
              {expanded && (
                <motion.div
                  key="gallery-rest"
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.45, ease: [0.2, 0.7, 0.2, 1] }}
                  className="overflow-hidden"
                >
                  <div
                    className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3 mt-3 md:mt-4 auto-rows-[180px] md:auto-rows-[220px]"
                    data-testid="gallery-grid"
                  >
                    {rest.map((url, i) => {
                      const spans = ["row-span-2", "", "", "row-span-2", "", "", "row-span-2", "", ""];
                      return (
                        <motion.div
                          key={`r-${i}`}
                          initial={{ opacity: 0, scale: 0.98 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ duration: 0.45, delay: i * 0.04 }}
                          className={`relative overflow-hidden bg-[#161616] group ${spans[i % spans.length]}`}
                          data-testid={`gallery-img-${i + PREVIEW_COUNT}`}
                        >
                          <img
                            src={url}
                            alt={`Team Astana — photo by ${photographer}`}
                            loading="lazy"
                            referrerPolicy="no-referrer"
                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                          />
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors" />
                        </motion.div>
                      );
                    })}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            <div className="mt-6 md:mt-8 flex justify-center">
              <button
                onClick={() => setExpanded((v) => !v)}
                data-testid="gallery-toggle"
                className="inline-flex items-center gap-2 px-5 py-2.5 border border-white/20 hover:border-[#FFDE00]/70 hover:text-[#FFDE00] transition-colors text-xs tracking-widest uppercase font-semibold"
              >
                {expanded ? (
                  <>
                    <ChevronUp size={14} /> {lang === "ru" ? "Свернуть" : "Show less"}
                  </>
                ) : (
                  <>
                    <ChevronDown size={14} />{" "}
                    {lang === "ru" ? "Показать все фото" : "Show all photos"}{" "}
                    <span className="text-[#FFDE00]/80">({rest.length})</span>
                  </>
                )}
              </button>
            </div>
          </>
        )}

        <div className="mt-8 text-xs text-white/40 tracking-widest uppercase">
          © {photographer}
        </div>
      </div>
    </section>
  );
};

export default Gallery;
